import {
  CERAMIC_BOOKING_DEPOSIT_CENTS,
  getConfiguredDepositCents,
  packageKeys,
  protectionAddOnKeys,
  type PackageKey,
} from './booking-domain.ts'

export const BOOKING_DEPOSIT_CURRENCY = 'AUD' as const
export const BOOKING_PUBLIC_REFERENCE_PATTERN = /^AGB-\d{4}-[A-F0-9]{6,12}$/i
export const STRIPE_CHECKOUT_SESSION_ID_PATTERN = /^cs_(test|live)_[A-Za-z0-9]+$/

export type DepositStatus =
  | 'not_started'
  | 'checkout_created'
  | 'pending'
  | 'paid'
  | 'failed'
  | 'cancelled'

export type BookingPaymentStatus =
  | 'unpaid'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'refunded'
  | 'partially_refunded'

export interface DepositCheckoutRequest {
  packageKey: PackageKey
  addOnIds: string[]
  noAdditionalProtection: boolean
  publicReference: string
}

const CLIENT_AMOUNT_KEYS = [
  'amount',
  'amountCents',
  'depositAmount',
  'depositAmountCents',
  'unit_amount',
  'unitAmount',
  'price',
  'total',
] as const

export function bookingDepositAmountCents(): number {
  return getConfiguredDepositCents()
}

export function bookingDepositCurrency(): typeof BOOKING_DEPOSIT_CURRENCY {
  return BOOKING_DEPOSIT_CURRENCY
}

/** Server-side amount is a business constant. Client fields are ignored. */
export function resolveDepositAmountCents(_untrustedBody?: unknown): number {
  return CERAMIC_BOOKING_DEPOSIT_CENTS
}

export function clientTriedToOverrideDepositAmount(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false
  return CLIENT_AMOUNT_KEYS.some((key) => key in body)
}

export function mapPaymentStatusToDepositStatus(
  paymentStatus?: string | null,
): DepositStatus {
  switch (paymentStatus) {
    case 'paid':
      return 'paid'
    case 'failed':
      return 'failed'
    case 'processing':
      return 'checkout_created'
    default:
      return 'not_started'
  }
}

/** Paid deposit is required to confirm an appointment, not to choose one. */
export function canLeaveDepositStep(depositStatus: DepositStatus): boolean {
  return depositStatus === 'paid'
}

export function canEnterConfirmationStep(
  depositStatus: DepositStatus,
  appointmentConfirmed: boolean,
): boolean {
  return depositStatus === 'paid' && appointmentConfirmed
}

export function isDuplicateWebhookEvent(
  processedEventIds: Iterable<string>,
  eventId: string,
): boolean {
  return new Set(processedEventIds).has(eventId)
}

export function stripeAmountMatchesDeposit(amountTotal?: number | null): boolean {
  return amountTotal == null || amountTotal === CERAMIC_BOOKING_DEPOSIT_CENTS
}

export function parseCheckoutReturnSearch(search: string): {
  checkout: 'return' | 'cancelled' | null
  sessionId: string | null
} {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const checkout = params.get('checkout')
  const sessionId = params.get('session_id')?.trim() ?? null
  const validSession =
    sessionId && STRIPE_CHECKOUT_SESSION_ID_PATTERN.test(sessionId) ? sessionId : null

  if (checkout === 'cancelled') {
    return { checkout: 'cancelled', sessionId: validSession }
  }
  if (checkout === 'return') {
    return { checkout: 'return', sessionId: validSession }
  }
  return { checkout: null, sessionId: validSession }
}

export const DEFAULT_PRODUCTION_SITE_URL = 'https://autoglowdetailing.com.au'
export const DEFAULT_DEVELOPMENT_SITE_URL = 'https://autoglow.pages.dev'
export const LOCAL_CHECKOUT_ORIGINS = ['http://localhost:3000', 'http://127.0.0.1:3000'] as const

export type StripeKeyMode = 'test' | 'live' | 'unknown' | 'unconfigured'

export function normalizeOrigin(origin: string) {
  return origin.replace(/\/$/, '')
}

export function isAutoglowPagesOrigin(origin: string) {
  try {
    const { protocol, hostname } = new URL(origin)
    if (protocol !== 'https:') return false
    return hostname === 'autoglow.pages.dev' || hostname.endsWith('.autoglow.pages.dev')
  } catch {
    return false
  }
}

export function resolveTrustedCheckoutBaseUrl(input: {
  stripeMode: StripeKeyMode
  developmentSiteUrl?: string | null
  productionSiteUrl?: string | null
  publicSiteUrl?: string | null
}) {
  const production = normalizeOrigin(
    input.productionSiteUrl
      || input.publicSiteUrl
      || DEFAULT_PRODUCTION_SITE_URL,
  )
  const development = normalizeOrigin(input.developmentSiteUrl || DEFAULT_DEVELOPMENT_SITE_URL)
  if (input.stripeMode === 'live') return production
  return development
}

export function allowedCheckoutReturnOrigins(input: {
  stripeMode: StripeKeyMode
  trustedBaseUrl: string
  developmentSiteUrl?: string | null
  productionSiteUrl?: string | null
}) {
  const trusted = normalizeOrigin(input.trustedBaseUrl)
  const development = normalizeOrigin(input.developmentSiteUrl || DEFAULT_DEVELOPMENT_SITE_URL)
  const production = normalizeOrigin(input.productionSiteUrl || DEFAULT_PRODUCTION_SITE_URL)
  const allowed = new Set<string>([trusted, ...LOCAL_CHECKOUT_ORIGINS])
  if (input.stripeMode === 'live') {
    allowed.add(production)
  } else {
    allowed.add(development)
  }
  return allowed
}

export function isAllowedCheckoutReturnOrigin(
  origin: string | null,
  trustedBaseUrl: string,
  options: {
    stripeMode?: StripeKeyMode
    developmentSiteUrl?: string | null
    productionSiteUrl?: string | null
  } = {},
): boolean {
  if (!origin) return false
  const normalised = normalizeOrigin(origin)
  const stripeMode = options.stripeMode ?? 'test'
  if (stripeMode !== 'live' && isAutoglowPagesOrigin(normalised)) return true
  return allowedCheckoutReturnOrigins({
    stripeMode,
    trustedBaseUrl,
    developmentSiteUrl: options.developmentSiteUrl,
    productionSiteUrl: options.productionSiteUrl,
  }).has(normalised)
}

/**
 * Test-mode Checkout never falls back to the live production domain.
 * An allowed development origin (localhost or Pages) wins; otherwise the
 * trusted development base URL is used.
 */
export function checkoutReturnBaseUrl(
  origin: string | null,
  trustedBaseUrl: string,
  options: {
    stripeMode?: StripeKeyMode
    developmentSiteUrl?: string | null
    productionSiteUrl?: string | null
  } = {},
): string {
  const stripeMode = options.stripeMode ?? 'test'
  if (isAllowedCheckoutReturnOrigin(origin, trustedBaseUrl, { ...options, stripeMode })) {
    return normalizeOrigin(origin!)
  }
  return normalizeOrigin(trustedBaseUrl)
}

export function buildDepositCheckoutUrls(baseUrl: string) {
  return {
    successUrl: `${baseUrl}/book?checkout=return&session_id={CHECKOUT_SESSION_ID}`,
    cancelUrl: `${baseUrl}/book?checkout=cancelled`,
  }
}

export function parseDepositCheckoutRequest(body: unknown): DepositCheckoutRequest {
  if (!body || typeof body !== 'object') {
    throw new Error('Invalid deposit checkout request.')
  }

  const record = body as Record<string, unknown>
  const packageKey = record.packageKey
  if (typeof packageKey !== 'string' || !packageKeys.includes(packageKey as PackageKey)) {
    throw new Error('Select a ceramic protection level before paying the deposit.')
  }

  const addOnIds = Array.isArray(record.addOns)
    ? record.addOns.filter((item): item is string => typeof item === 'string')
    : []
  const noAdditionalProtection = addOnIds.length === 0

  if (addOnIds.some((id) => !protectionAddOnKeys.includes(id as typeof protectionAddOnKeys[number]))) {
    throw new Error('One or more add-ons are not recognised.')
  }

  const publicReference =
    typeof record.publicReference === 'string' && BOOKING_PUBLIC_REFERENCE_PATTERN.test(record.publicReference)
      ? record.publicReference
      : undefined

  if (!publicReference) {
    throw new Error('Reserve an appointment before paying the deposit.')
  }

  return {
    packageKey: packageKey as PackageKey,
    addOnIds,
    noAdditionalProtection,
    publicReference,
  }
}

export function checkoutMetadata(input: {
  bookingId: string
  bookingReference: string
  packageKey: string
  addOnIds: string[]
}) {
  return {
    booking_id: input.bookingId,
    booking_reference: input.bookingReference,
    package_key: input.packageKey,
    add_on_ids: input.addOnIds.join(','),
  }
}
